import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { shopOrderService, type RestockItem } from '@/services/shopOrderService'
import { OrderStatus } from '@/types/shop'
import type {
  Order,
  OrderListItem,
  FulfillmentType,
  OrderStatistics,
  UpdateOrderStatusRequest,
  OrderFilters
} from '@/types/shop'
import { isSessionExpiredError } from '@/services/api'

export const useShopOrderStore = defineStore('shopOrder', () => {
  // ============================================================================
  // State
  // ============================================================================

  const orders = ref<OrderListItem[]>([])
  const currentOrder = ref<Order | null>(null)
  const pendingOrders = ref<OrderListItem[]>([])
  const todayOrders = ref<OrderListItem[]>([])
  const orderStatistics = ref<OrderStatistics | null>(null)
  const totalRevenue = ref<number>(0)

  // Pagination & filtering
  const currentPage = ref(1)
  const pageSize = ref(20)
  const totalCount = ref(0)
  const totalPages = ref(0)
  const filters = ref<OrderFilters>({})

  // Loading states
  const loading = ref(false)
  const loadingOrder = ref(false)
  const loadingPending = ref(false)
  const loadingToday = ref(false)
  const loadingStatistics = ref(false)
  const saving = ref(false)

  // ============================================================================
  // Computed
  // ============================================================================

  const hasOrders = computed(() => orders.value.length > 0)

  const hasPendingOrders = computed(() => pendingOrders.value.length > 0)

  const pendingOrderCount = computed(() => pendingOrders.value.length)

  const todayOrderCount = computed(() => todayOrders.value.length)

  const orderCount = computed(() => totalCount.value)

  const ordersByStatus = computed(() => {
    const grouped: Record<OrderStatus, OrderListItem[]> = {
      [OrderStatus.Draft]: [],
      [OrderStatus.PendingPayment]: [],
      [OrderStatus.PaymentFailed]: [],
      [OrderStatus.Paid]: [],
      [OrderStatus.PartiallyPaid]: [],
      [OrderStatus.Unpaid]: [],
      [OrderStatus.AwaitingCustomerResponse]: [],
      [OrderStatus.Confirmed]: [],
      [OrderStatus.Processing]: [],
      [OrderStatus.ReadyForCollection]: [],
      [OrderStatus.OutForDelivery]: [],
      [OrderStatus.Completed]: [],
      [OrderStatus.Cancelled]: [],
      [OrderStatus.Refunded]: [],
      [OrderStatus.PartiallyRefunded]: [],
      [OrderStatus.Abandoned]: []
    }
    orders.value.forEach(order => {
      if (grouped[order.status]) {
        grouped[order.status].push(order)
      }
    })
    return grouped
  })

  // ============================================================================
  // Provider Order Management
  // ============================================================================

  /**
   * Fetch orders for a shop
   */
  async function fetchOrders(shopId: string, newFilters?: OrderFilters) {
    loading.value = true
    try {
      if (newFilters) {
        filters.value = { ...filters.value, ...newFilters }
      }
      const result = await shopOrderService.getOrders(shopId, {
        ...filters.value,
        page: currentPage.value,
        pageSize: pageSize.value
      })
      orders.value = result.items
      totalCount.value = result.totalCount
      totalPages.value = result.totalPages
      currentPage.value = result.page
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching orders:', error)
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch pending orders
   */
  async function fetchPendingOrders(shopId: string) {
    loadingPending.value = true
    try {
      pendingOrders.value = await shopOrderService.getPendingOrders(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching pending orders:', error)
      }
      throw error
    } finally {
      loadingPending.value = false
    }
  }

  /**
   * Fetch today's orders
   */
  async function fetchTodayOrders(shopId: string) {
    loadingToday.value = true
    try {
      todayOrders.value = await shopOrderService.getTodayOrders(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching today orders:', error)
      }
      throw error
    } finally {
      loadingToday.value = false
    }
  }

  /**
   * Fetch order by ID
   */
  async function fetchOrder(id: string) {
    loadingOrder.value = true
    try {
      const order = await shopOrderService.getOrder(id)
      currentOrder.value = order
      return order
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching order:', error)
      }
      throw error
    } finally {
      loadingOrder.value = false
    }
  }

  /**
   * Update order status
   */
  async function updateOrderStatus(id: string, data: UpdateOrderStatusRequest) {
    saving.value = true
    try {
      const order = await shopOrderService.updateOrderStatus(id, data)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Confirm order
   */
  async function confirmOrder(id: string, notes?: string) {
    saving.value = true
    try {
      const order = await shopOrderService.confirmOrder(id, notes)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error confirming order:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Accept order (simplified workflow - skips Confirmed, goes directly to Processing)
   */
  async function acceptOrder(id: string, notes?: string) {
    saving.value = true
    try {
      const order = await shopOrderService.acceptOrder(id, notes)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error accepting order:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Start processing order
   */
  async function startProcessing(id: string, notes?: string) {
    saving.value = true
    try {
      const order = await shopOrderService.startProcessing(id, notes)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error starting processing:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Mark order as ready
   */
  async function markAsReady(id: string, notes?: string) {
    saving.value = true
    try {
      const order = await shopOrderService.markAsReady(id, notes)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error marking as ready:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Complete order
   */
  async function completeOrder(id: string, notes?: string) {
    saving.value = true
    try {
      const order = await shopOrderService.completeOrder(id, notes)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error completing order:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Cancel order with optional refund. `refundMethod` + `refundReference`
   * mirror `refundOrder` below — only consulted when `processRefund` is
   * true. Default `Original` = Stripe (unchanged legacy behaviour), manual
   * methods skip Stripe and record a ledger row.
   */
  async function cancelOrder(
    id: string,
    reason?: string,
    processRefund: boolean = false,
    refundAmount?: number,
    refundMethod: 'Original' | 'Cash' | 'Card' | 'BankTransfer' | 'Other' = 'Original',
    refundReference?: string,
  ) {
    saving.value = true
    try {
      const result = await shopOrderService.cancelOrder(
        id,
        reason,
        processRefund,
        refundAmount,
        refundMethod,
        refundReference,
      )
      updateOrderInLists(result.order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = result.order
      }
      return result
    } catch (error) {
      console.error('Error cancelling order:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Refund order. Passes through the refund method + optional reference to
   * the service; see shopOrderService.refundOrder for method semantics.
   */
  async function refundOrder(
    id: string,
    amount: number,
    reason?: string,
    refundMethod: 'Original' | 'Cash' | 'Card' | 'BankTransfer' | 'Other' = 'Original',
    refundReference?: string,
    restockItems?: RestockItem[],
  ) {
    saving.value = true
    try {
      const result = await shopOrderService.refundOrder(id, amount, reason, refundMethod, refundReference, restockItems)
      updateOrderInLists(result.order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = result.order
      }
      return result
    } catch (error) {
      console.error('Error refunding order:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Collect payment for POD (Pay on Delivery/Collection) orders
   * @param id - Order ID
   * @param amountCollected - Amount collected (defaults to full amount due)
   * @param podPaymentMethod - How payment was collected: 'Cash', 'Card' or 'BankTransfer'
   */
  async function collectPayment(
    id: string,
    amountCollected?: number,
    podPaymentMethod?: 'Cash' | 'Card' | 'BankTransfer'
  ) {
    saving.value = true
    try {
      const order = await shopOrderService.collectPayment(id, amountCollected, podPaymentMethod)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error collecting payment:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Collect an extra balance owed after a mid-flow amendment pushed the
   * total above what the customer paid. Refreshes currentOrder + list
   * entries so the amber "Extra to collect" banner disappears once the
   * BE zeros out `extraToCollectAmount`.
   */
  async function collectExtraPayment(
    id: string,
    amount: number,
    method: 'Cash' | 'Card' | 'BankTransfer' | 'Other',
    note?: string
  ) {
    saving.value = true
    try {
      const order = await shopOrderService.collectExtraPayment(id, amount, method, note)
      updateOrderInLists(order)
      if (currentOrder.value?.id === id) {
        currentOrder.value = order
      }
      return order
    } catch (error) {
      console.error('Error collecting extra payment:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Contact customer about item availability issues (POD orders only)
   * Creates an availability issue and sends an email to the customer with accept/cancel options.
   */
  async function contactCustomerAvailability(
    orderId: string,
    items: Array<{
      orderItemId: string
      availableQuantity?: number
      isUnavailable: boolean
      alternativeProductId?: string
      alternativeVariantId?: string
      alternativeQuantity?: number
      alternativeNote?: string
      alternativeOptionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
    }>,
    message?: string,
    newLines?: Array<{
      productId: string
      productVariantId?: string
      quantity: number
      notes?: string
      optionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
      isExtra?: boolean
      productExtraId?: string
    }>
  ) {
    saving.value = true
    try {
      const result = await shopOrderService.contactCustomerAvailability(orderId, { items, message, newLines })
      // The order status should be updated to AwaitingCustomerResponse
      // Refresh the order to get the updated status
      const order = await shopOrderService.getOrder(orderId)
      updateOrderInLists(order)
      if (currentOrder.value?.id === orderId) {
        currentOrder.value = order
      }
      return result
    } catch (error) {
      console.error('Error contacting customer:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Fetch order statistics
   */
  async function fetchOrderStatistics(shopId: string, fromDate?: string, toDate?: string) {
    loadingStatistics.value = true
    try {
      orderStatistics.value = await shopOrderService.getOrderStatistics(shopId, fromDate, toDate)
      return orderStatistics.value
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching order statistics:', error)
      }
      throw error
    } finally {
      loadingStatistics.value = false
    }
  }

  /**
   * Fetch total revenue
   */
  async function fetchRevenue(shopId: string, fromDate?: string, toDate?: string) {
    try {
      const result = await shopOrderService.getRevenue(shopId, fromDate, toDate)
      totalRevenue.value = result.totalRevenue
      return result.totalRevenue
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching revenue:', error)
      }
      throw error
    }
  }

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Update order in all relevant lists
   */
  function updateOrderInLists(order: Order) {
    // Update in main orders list
    const index = orders.value.findIndex(o => o.id === order.id)
    if (index !== -1) {
      // Merge into existing row instead of rebuilding from scratch — the
      // status-update response only carries the fields the server changed,
      // so a full rebuild would drop list-only metadata (source, channel,
      // origin hostname) and revert the Channel column to its default.
      orders.value[index] = {
        ...orders.value[index],
        status: order.status,
        fulfillmentType: order.fulfillmentType,
        customerName: order.customerName,
        customerEmail: order.customerEmail,
        itemCount: order.items.length,
        totalAmount: order.totalAmount,
        currency: order.currency,
        paidAt: order.paidAt,
        paymentMethod: order.paymentMethod,
        paymentMethodDisplay: order.paymentMethodDisplay,
        paymentStatus: order.paymentStatus,
        paymentStatusDisplay: order.paymentStatusDisplay,
        amountDue: order.amountDue,
        canCollectPayment: order.canCollectPayment,
        refundedAmount: order.refundedAmount || 0,
        hasRefund: (order.refundedAmount || 0) > 0
      }
    }

    // Update pending orders
    const pendingIndex = pendingOrders.value.findIndex(o => o.id === order.id)
    const isPending = ['Paid', 'Confirmed', 'Processing'].includes(order.status)
    if (pendingIndex !== -1 && !isPending) {
      // Remove from pending if no longer pending
      pendingOrders.value.splice(pendingIndex, 1)
    } else if (pendingIndex !== -1) {
      // Update if still pending
      pendingOrders.value[pendingIndex] = {
        ...pendingOrders.value[pendingIndex],
        status: order.status
      }
    }

    // Update today orders
    const todayIndex = todayOrders.value.findIndex(o => o.id === order.id)
    if (todayIndex !== -1) {
      todayOrders.value[todayIndex] = {
        ...todayOrders.value[todayIndex],
        status: order.status
      }
    }
  }

  // ============================================================================
  // Pagination & Filtering
  // ============================================================================

  function setPage(page: number, shopId: string) {
    currentPage.value = page
    fetchOrders(shopId)
  }

  function setPageSize(size: number, shopId: string) {
    pageSize.value = size
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function setFilters(newFilters: OrderFilters, shopId: string) {
    filters.value = newFilters
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function setStatusFilter(status: OrderStatus | undefined, shopId: string) {
    filters.value = { ...filters.value, status }
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function setFulfillmentFilter(fulfillmentType: FulfillmentType | undefined, shopId: string) {
    filters.value = { ...filters.value, fulfillmentType }
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function setDateRangeFilter(fromDate: string | undefined, toDate: string | undefined, shopId: string) {
    filters.value = { ...filters.value, fromDate, toDate }
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function setSearchFilter(search: string, shopId: string) {
    filters.value = { ...filters.value, search }
    currentPage.value = 1
    fetchOrders(shopId)
  }

  function clearFilters(shopId: string) {
    filters.value = {}
    currentPage.value = 1
    fetchOrders(shopId)
  }

  // ============================================================================
  // Reset
  // ============================================================================

  function resetStore() {
    orders.value = []
    currentOrder.value = null
    pendingOrders.value = []
    todayOrders.value = []
    orderStatistics.value = null
    totalRevenue.value = 0
    currentPage.value = 1
    totalCount.value = 0
    totalPages.value = 0
    filters.value = {}
  }

  return {
    // State
    orders,
    currentOrder,
    pendingOrders,
    todayOrders,
    orderStatistics,
    totalRevenue,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    filters,
    loading,
    loadingOrder,
    loadingPending,
    loadingToday,
    loadingStatistics,
    saving,

    // Computed
    hasOrders,
    hasPendingOrders,
    pendingOrderCount,
    todayOrderCount,
    orderCount,
    ordersByStatus,

    // Actions
    fetchOrders,
    fetchPendingOrders,
    fetchTodayOrders,
    fetchOrder,
    updateOrderStatus,
    confirmOrder,
    acceptOrder,
    startProcessing,
    markAsReady,
    completeOrder,
    cancelOrder,
    refundOrder,
    collectPayment,
    collectExtraPayment,
    contactCustomerAvailability,
    fetchOrderStatistics,
    fetchRevenue,

    // Pagination & Filtering
    setPage,
    setPageSize,
    setFilters,
    setStatusFilter,
    setFulfillmentFilter,
    setDateRangeFilter,
    setSearchFilter,
    clearFilters,

    // Reset
    resetStore
  }
})
